import { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Collapse,
  Group,
  Stack,
  Table,
  Text,
  UnstyledButton,
} from '@mantine/core';
import { AlertTriangle, ChevronDown, ChevronUp, X } from 'react-feather';
import {
  paperformDeletionsApi,
  type PaperformDeletionsBannerState,
} from '../../../services/api/paperformDeletions';

interface PaperformDeletionsBannerProps {
  cohortId: string;
}

const FORM_TYPE_LABELS: Record<string, string> = {
  intake: 'intake',
  mid_program: 'mid-program survey',
  final_survey: 'final survey',
  session_feedback: 'session feedback',
  other: 'submission',
};

function formatBreakdown(counts: PaperformDeletionsBannerState['countsByFormType']): string {
  if (counts.length === 0) return '';
  return counts
    .map(c => {
      const label = FORM_TYPE_LABELS[c.form_type] ?? c.form_type.replace(/_/g, ' ');
      return `${c.count} ${label}${c.count === 1 ? '' : 's'}`;
    })
    .join(', ');
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

export function PaperformDeletionsBanner({ cohortId }: PaperformDeletionsBannerProps) {
  const [state, setState] = useState<PaperformDeletionsBannerState | null>(null);
  const [expanded, setExpanded] = useState(false);
  const [dismissing, setDismissing] = useState(false);

  const fetchState = useCallback(async () => {
    try {
      const data = await paperformDeletionsApi.getBannerState(cohortId);
      setState(data);
    } catch (error) {
      // MED-903: Silent failure — this banner is supplementary signal, not
      // critical UX. A failed fetch should never block the Participants tab.
      console.error('Failed to fetch Paperform deletions banner state:', error);
      setState(null);
    }
  }, [cohortId]);

  useEffect(() => {
    fetchState();
  }, [fetchState]);

  const handleDismiss = async () => {
    setDismissing(true);
    try {
      await paperformDeletionsApi.dismiss(cohortId);
      setState(prev => (prev ? { ...prev, shouldShow: false } : prev));
    } catch (error) {
      console.error('Failed to dismiss Paperform deletions banner:', error);
    } finally {
      setDismissing(false);
    }
  };

  if (!state || !state.shouldShow) return null;

  const breakdown = formatBreakdown(state.countsByFormType);
  const submissionWord = state.totalCount === 1 ? 'submission' : 'submissions';
  const wasWord = state.totalCount === 1 ? 'was' : 'were';
  const summaryText = `${state.totalCount} ${submissionWord} for this cohort ${wasWord} removed from Paperform in the last ${state.windowDays} days${breakdown ? ` (${breakdown})` : ''}.`;

  return (
    <Box
      style={{
        background: '#FEF6E7',
        border: '1px solid #E9A23B',
        borderRadius: 8,
        padding: '14px 18px',
      }}
    >
      <Group justify="space-between" align="flex-start" wrap="nowrap">
        <Group gap="md" align="center" wrap="nowrap" style={{ flex: 1, minWidth: 0 }}>
          <Box
            style={{
              width: 36,
              height: 36,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: '#FAE3B6',
              flexShrink: 0,
            }}
          >
            <AlertTriangle size={18} color="#9C6A14" />
          </Box>
          <Stack gap={2} style={{ minWidth: 0 }}>
            <Text fw={600} size="sm" c="#1A1A1A">
              Paperform submissions removed
            </Text>
            <Text size="xs" c="#666666">
              {summaryText}
            </Text>
          </Stack>
        </Group>
        <Group gap="xs" wrap="nowrap" style={{ flexShrink: 0 }}>
          <UnstyledButton
            onClick={() => setExpanded(prev => !prev)}
            aria-expanded={expanded}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 4,
              fontSize: 13,
              color: '#2D4739',
              fontWeight: 500,
            }}
          >
            View details{' '}
            {expanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
          </UnstyledButton>
          <Button
            variant="subtle"
            color="gray"
            size="xs"
            leftSection={<X size={14} />}
            onClick={handleDismiss}
            loading={dismissing}
          >
            Dismiss
          </Button>
        </Group>
      </Group>

      <Collapse in={expanded}>
        <Box mt="sm" style={{ borderTop: '1px solid #F4D58D', paddingTop: 12 }}>
          {state.deletions.length === 0 ? (
            <Text size="xs" c="dimmed">
              No detail rows to display.
            </Text>
          ) : (
            <Table verticalSpacing={6} withRowBorders={false} style={{ fontSize: 12 }}>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Participant</Table.Th>
                  <Table.Th>Form type</Table.Th>
                  <Table.Th>Submitted</Table.Th>
                  <Table.Th>Deleted</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {state.deletions.map(d => (
                  <Table.Tr key={d.id}>
                    <Table.Td>{d.participant_name ?? '—'}</Table.Td>
                    <Table.Td>
                      {FORM_TYPE_LABELS[d.form_type] ?? d.form_type.replace(/_/g, ' ')}
                    </Table.Td>
                    <Table.Td>{formatDate(d.submitted_at)}</Table.Td>
                    <Table.Td>{formatDate(d.deleted_from_paperform_at)}</Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          )}
        </Box>
      </Collapse>
    </Box>
  );
}
